/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  CheckUsageDto,
  CSMasterCCDto,
  CSMasterDetailsDto,
  CsMasterFilterDto,
  CSMasterHeadingDto,
  CSMasterItemDto,
  CSMasterListItemDto,
  CSMasterListItemStatus,
  CSMasterSubHeadingDto,
} from '@gh/builder-shared-data';
import {
  entityValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  EntityVersion,
  ID,
  withDisplayAsNameAndDescription,
} from '@gh/common-shared-data';
import { createTreeHelper } from '@gh/core-ui-ext/tree';
import { compact, pick, uniqueId } from 'lodash';

export enum CSMasterSelectionLevel { Heading = 1, SubHeading = 2, Item = 3, CostCenter = 4 }

export const cSMasterTreeHelper = createTreeHelper(['children', 'children', 'children']);

export type CSMasterSelection = {
  readonly level?: CSMasterSelectionLevel;
  readonly heading?: CSMasterHeadingInfo;
  readonly subHeading?: CSMasterSubHeadingInfo;
  readonly item?: CSMasterItemInfo;
  readonly costCenter?: CSMasterCCInfo;
};

export type CSMasterListItem = CSMasterListItemDto;
export type CsMasterFilter = CsMasterFilterDto;

export type CSMasterCheckUsage = CheckUsageDto;

export function isInactive({ data }: { data: CSMasterListItem }): boolean {
  return data && data.statusValue === CSMasterListItemStatus.Inactive;
}

export type CSMasterInfo = {
  name: string;
};

const newCSMasterDetailsMasterInfo = () => formStateSnapshot({
  name: '',
});

export const newCSMasterDetails = () => <CSMasterInfoDetails>({
  masterInfo: newCSMasterDetailsMasterInfo(),
  headings: [],
});

export type CSMasterSubHeading = {
  name: string;
};
export type CSMasterHeading = {
  name: string;
  isNewPage: boolean;
};
export type CSMasterItem = {
  name: string;
  description: string;
};
export type CSMasterCC = {
  costCenter: AutocompleteItem;
};

export type CSMasterInfoDetails = {
  id?: ID;
  version?: EntityVersion;
  status?: number;
  masterInfo: FormStateSnapshot<CSMasterInfo>;
  headings: CSMasterHeadingInfo[];
  isNew?: boolean;
};

export type CSMasterHeadingInfo = {
  id?: ID;
  csMasterHeadingId?: ID;
  info: FormStateSnapshot<CSMasterHeading>;
  children: CSMasterSubHeadingInfo[];
  isNewPage?: boolean;
  isNew?: boolean;
};

export type CSMasterSubHeadingInfo = {
  id?: ID;
  csMasterSubHeadingId?: ID;
  info: FormStateSnapshot<CSMasterSubHeading>;
  children: CSMasterItemInfo[];
  isNew?: boolean;
};

export type CSMasterItemInfo = {
  id?: ID;
  csMasterItemId?: ID;
  info: FormStateSnapshot<CSMasterItem>;
  children: CSMasterCCInfo[];
  isNew?: boolean;
};

export type CSMasterCCInfo = {
  id?: ID;
  info: FormStateSnapshot<CSMasterCC>;
  isNew?: boolean;
};

const cSMasterCCFromDto = (dto: CSMasterCCDto) => <CSMasterCCInfo>({
  id: uniqueId('csm-cc:'),
  info: formStateSnapshot({
    costCenter: withDisplayAsNameAndDescription({
      id: dto.costCenterId,
      name: dto.code,
      description: dto.description,
    }),
  }),
});

const cSMasterItemFromDto = (dto: CSMasterItemDto) => <CSMasterItemInfo>({
  id: uniqueId('csm-item:'),
  csMasterItemId: dto.csMasterItemId,
  info: formStateSnapshot(pick(dto, ['name', 'description'])),
  children: dto.costCenters ? dto.costCenters.map(cSMasterCCFromDto) : [],
});

const cSMasterSubHeadingFromDto = (dto: CSMasterSubHeadingDto) => <CSMasterSubHeadingInfo>({
  id: uniqueId('csm-subhead:'),
  csMasterSubHeadingId: dto.csMasterSubHeadingId,
  info: formStateSnapshot(pick(dto, ['name'])),
  children: dto.items ? dto.items.map(cSMasterItemFromDto) : [],
});

const cSMaterHeadingFromDto = (dto: CSMasterHeadingDto) => <CSMasterHeadingInfo>({
  id: uniqueId('csm-head:'),
  csMasterHeadingId: dto.csMasterHeadingId,
  info: formStateSnapshot(pick(dto, ['name', 'isNewPage'])),
  children: dto.subHeadings ? dto.subHeadings.map(cSMasterSubHeadingFromDto) : [],
});

export const cSMasterDetailsFromDto = (dto: CSMasterDetailsDto) => <CSMasterInfoDetails>({
  id: dto.csMasterId,
  version: dto.version,
  status: dto.status,
  masterInfo: formStateSnapshot(pick(dto, ['name'])),
  headings: dto.headings ? dto.headings.map(cSMaterHeadingFromDto) : [],
});

export const toClonedCSMasterDetails = (details: CSMasterInfoDetails) => ({
  ...details,
  id: void 0,
  masterInfo: formStateSnapshot(({ name: '' })),
  headings: details.headings.map((heading) => <CSMasterHeadingInfo>{
    ...heading,
    isNew: true,
    children: heading.children.map((subheading) => <CSMasterSubHeadingInfo>{
      ...subheading,
      isNew: true,
      children: subheading.children.map((item) => <CSMasterItemInfo>{
        ...item,
        isNew: true,
      }),
    }),
  }),
});

export const cSMasterCCToDto = ({ id, info }: CSMasterCCInfo) => <CSMasterCCDto>({
  costCenterId: getFormStateSnapshotValue(info).costCenter!.id,
  code: getFormStateSnapshotValue(info).costCenter!.name,
  description: getFormStateSnapshotValue(info).costCenter!.display,
});

export const cSMasterItemToDto = ({ csMasterItemId, info, children, isNew }: CSMasterItemInfo) =>
  <CSMasterItemDto>({
    csMasterItemId: isNew ? void 0 : csMasterItemId,
    ...pick(getFormStateSnapshotValue(info), 'name', 'description'),
    costCenters: children.map(cSMasterCCToDto),
  });

export const cSMasterSubHeadingToDto = ({ csMasterSubHeadingId, info, children, isNew }: CSMasterSubHeadingInfo) =>
  <CSMasterSubHeadingDto>({
    csMasterSubHeadingId: isNew ? void 0 : csMasterSubHeadingId,
    ...pick(getFormStateSnapshotValue(info), 'name'),
    items: children.map(cSMasterItemToDto),
  });

export const cSMasterHeadingToDto = ({ csMasterHeadingId, info, children, isNew }: CSMasterHeadingInfo) =>
  <CSMasterHeadingDto>({
    csMasterHeadingId: isNew ? void 0 : csMasterHeadingId,
// @ts-expect-error Legacy typing suppression
    isNewPage: false,
    ...pick(getFormStateSnapshotValue(info), 'name', 'isNewPage'),
    subHeadings: children.map(cSMasterSubHeadingToDto),
  });

export const cSMasterDetailsToDto = ({ id, version, masterInfo, headings, status }: CSMasterInfoDetails) =>
  <CSMasterDetailsDto>({
    csMasterId: id,
    version,
    status: status || 1,
    ...getFormStateSnapshotValue(masterInfo),
    headings: headings.map(cSMasterHeadingToDto),
  });

export const cSMasterSelectionToTreePath = ({ level, heading, subHeading, item, costCenter }: CSMasterSelection) =>
  level ? compact([heading && heading.id, subHeading && subHeading.id,
    item && item.id, costCenter && costCenter.id]) : void 0;

export const CS_MASTER_INFO_ENTITY_CONFIG = { name: { required: true } };
export const CS_MASTER_HEADING_INFO_ENTITY_CONFIG = { name: { required: true } };
export const CS_MASTER_SUB_HEADING_INFO_ENTITY_CONFIG = { name: { required: true } };
export const CS_MASTER_ITEM_INFO_ENTITY_CONFIG = {
  name: { required: true },
  description: {},
};
export const CS_MASTER_CC_INFO_ENTITY_CONFIG = {
  costCenter: { required: true },
};

export const validateCSMasterInfo = entityValidatorByEntityConfig({
  ...CS_MASTER_INFO_ENTITY_CONFIG,
});

export const validateCSMasterHeadingInfo = entityValidatorByEntityConfig({
  ...CS_MASTER_HEADING_INFO_ENTITY_CONFIG,
});

export const validateCSMasterSubHeadingInfo = entityValidatorByEntityConfig({
  ...CS_MASTER_SUB_HEADING_INFO_ENTITY_CONFIG,
});

export const validateCSMasterItemInfo = entityValidatorByEntityConfig({
  ...CS_MASTER_ITEM_INFO_ENTITY_CONFIG,
});

export const validateCSMasterCCInfo = entityValidatorByEntityConfig({
  ...CS_MASTER_CC_INFO_ENTITY_CONFIG,
});

export enum CloneSource {
  CSMaster = 'CSMaster',
  JobCS = 'JobCS',
}

export const getCsMasterCcCode = (cc: CSMasterCCInfo) => Number(getFormStateSnapshotValue(cc.info).costCenter.name);
